// Hook for fetching and managing notifications

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import {
  getNotifications,
  markAllNotificationsAsRead,
} from '@/services/notifications/notificationService'
import { useAuthStore } from '@/stores/authStore'
import type { Notification } from '@/types/notification'

const NOTIFICATIONS_LIMIT = 30

interface NotificationsPage {
  notifications: Notification[]
  lastId: number | undefined
  hasMore: boolean
}

export function useNotifications() {
  const user = useAuthStore((state) => state.user)
  const username = user?.username

  return useInfiniteQuery<NotificationsPage>({
    queryKey: queryKeys.notificationsInfinite(username || ''),
    queryFn: async ({ pageParam }) => {
      if (!username) {
        return {
          notifications: [],
          lastId: undefined,
          hasMore: false,
        }
      }

      const lastId = pageParam as number | undefined
      const notifications = await getNotifications({
        account: username,
        limit: NOTIFICATIONS_LIMIT,
        lastId,
      })

      const newLastId =
        notifications.length > 0 ? notifications[notifications.length - 1]?.id : undefined

      return {
        notifications,
        lastId: newLastId,
        hasMore: notifications.length === NOTIFICATIONS_LIMIT,
      }
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastId : undefined),
    initialPageParam: undefined as number | undefined,
    enabled: !!username,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Poll every minute
  })
}

export function useMarkAllAsRead() {
  const user = useAuthStore((state) => state.user)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      const now = new Date().toISOString()
      const result = await markAllNotificationsAsRead({
        account: user.username,
        postingKey: user.postingKey,
        date: now,
      })

      if (!result.success) {
        throw new Error(result.error || 'Failed to mark notifications as read')
      }

      return result
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh the list
      if (user) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications(user.username),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsInfinite(user.username),
        })
      }
    },
  })
}
